import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { createApplicationApi, endClientSessionApi } from "../api/application";
import { useApiError } from "./ApiErrorProvider";
import { useClient } from "./ClientProvider";
import { handleApiError } from "../api/handleApiError";

/**
 * Client session — what replaced the OAuth AuthProvider.
 *
 * There is no account and no login. An applicant's "session" is simply an
 * application in progress: the server holds an anonymous session carrying the
 * engine token and the application id, and the browser holds the application id
 * so a mid-journey reload can resume.
 *
 * `endSession` is the replacement for the old OAuthLogout, which several parts of
 * the capture flow used as a general "abandon and start over" action — the timer
 * expiring, and the barcode-failure exit that sends a desktop user to mobile.
 */

const APPLICATION_ID_KEY = "applicationId";

interface ClientSessionContextType {
  /** Current application, or null when nothing is in progress. */
  applicationId: string | null;
  /** True while an application is being created. */
  isStarting: boolean;
  /** Creates an application and navigates into the capture flow. */
  startApplication: () => Promise<void>;
  /** Abandons the journey: clears local and server state, returns to the start. */
  endSession: () => void;
}

const ClientSessionContext = createContext<ClientSessionContextType | undefined>(
  undefined,
);

export const ClientSessionProvider = ({ children }: { children: ReactNode }) => {
  const [applicationId, setApplicationId] = useState<string | null>(() =>
    sessionStorage.getItem(APPLICATION_ID_KEY),
  );
  const [isStarting, setIsStarting] = useState(false);
  const navigate = useNavigate();
  // ClientProvider wraps this one, so its reset is available here.
  const { reset: resetClient } = useClient();
  const { setError } = useApiError();

  // Keep browser storage in step, so a reload mid-capture resumes rather than
  // bouncing the applicant back to the start.
  useEffect(() => {
    if (applicationId) {
      sessionStorage.setItem(APPLICATION_ID_KEY, applicationId);
    } else {
      sessionStorage.removeItem(APPLICATION_ID_KEY);
    }
  }, [applicationId]);

  const startApplication = useCallback(async () => {
    if (isStarting) return;
    setIsStarting(true);
    try {
      const response = await createApplicationApi();
      const id = response?.applicationId;
      if (id) {
        setApplicationId(id);
        setError(null);
        // A new application gets a new clock. Without this the Timer inherits
        // the previous one's start time from sessionStorage, so an applicant who
        // returns to a tab that has been open past the session duration mounts
        // the capture screen with an already-expired timer.
        sessionStorage.removeItem("timerStartTime");
        navigate(`/onboard/${id}`);
      }
    } catch (error) {
      handleApiError(error, setError, () => startApplication());
    } finally {
      setIsStarting(false);
    }
  }, [isStarting, navigate, setError]);

  const endSession = useCallback(() => {
    setApplicationId(null);
    sessionStorage.removeItem(APPLICATION_ID_KEY);
    sessionStorage.removeItem("timerStartTime");
    sessionStorage.removeItem("retake");
    sessionStorage.removeItem("PD_Type");
    sessionStorage.removeItem("SD_Type");
    sessionStorage.removeItem("SD_Image");
    /*
     * Forget the resolved client too, not just the application.
     *
     * ClientProvider sits above the router outlet and never remounts on
     * navigation, so its state outlives this one. Clearing only the application
     * left the applicant on a landing page that still held a resolved client and
     * their address, and so asked for an email address rather than a code — the
     * middle of the journey they had just finished.
     */
    resetClient();
    // Fire and forget: the applicant should land back at the start whether or
    // not the server acknowledges, and there is no identity provider to visit.
    void endClientSessionApi();
    navigate("/", { replace: true });
  }, [navigate, resetClient]);

  return (
    <ClientSessionContext.Provider
      value={{ applicationId, isStarting, startApplication, endSession }}
    >
      {children}
    </ClientSessionContext.Provider>
  );
};

export const useClientSession = (): ClientSessionContextType => {
  const context = useContext(ClientSessionContext);
  if (!context) {
    throw new Error(
      "useClientSession must be used within a ClientSessionProvider",
    );
  }
  return context;
};
